<template>
    <div
        v-if="ready"
        v-bind:class="{
            chat: currentView == 'chat',
            card: currentView == 'card',
            'chat--embed': embed,
            'chat--fullscreen': fullscreen,
        }"
    >
        <slot v-if="currentView == 'chat'" name="header" :status="status" :leave="leave">
            <header class="chat__header">
                <h1 class="chat__header__title">
                    {{ $t('chat.title') }}
                </h1>
            </header>
        </slot>

        <slot v-if="currentView == 'chat' && (status == 'chat' || status == 'ended')" name="nav" :status="status" :volunteer="volunteer"> </slot>

        <div
            v-if="currentView == 'card' || status == 'open' || status == 'closed' || status == 'max_queue' || status == 'close_queue' || status == 'close_day'"
            v-bind:class="{ chat__card: currentView == 'chat' }"
        >
            <slot name="card" :view="currentView" :status="status" :texts="texts" :site="site" :invites="invites" :start="start" :goto="goto" :quit="quit">
                <div v-if="status == 'open'">
                    <button v-on:click="start" class="button">Börja chatta</button>
                    <p v-if="texts.status" v-html="texts.status"></p>
                </div>

                <div v-else-if="status == 'queue'">
                    <button v-on:click="goto" class="button">Gå till chatten</button>
                    <p>
                        Om du har stängt chatt-fönstret, kan du öppna det på nytt
                        <a href="" v-on:click.stop="goto">här</a>. Du kan även avsluta <a href="" v-on:click.stop="quit">här</a>.
                    </p>
                </div>

                <div v-else-if="status == 'chat'">
                    <button v-on:click="goto" class="button">Gå till chatten</button>
                    <p>
                        Du har redan en pågående chatt. Har du inte stängt chatt-fönstret, kan du öppna det på nytt <a v-on:click.stop="goto">här</a>. Du kan
                        även avsluta <a href="" v-on:click.stop="quit">här</a>.
                    </p>
                </div>

                <div v-else-if="status == 'max_queue'">
                    <button class="button button--disabled">Chatten är just nu full</button>
                    <p v-if="texts.queue" v-html="texts.queue"></p>
                </div>

                <div v-else-if="status == 'close_day'">
                    <button class="button button--disabled">Chatten är stängd for ikväll</button>
                </div>

                <div v-else-if="status == 'close_queue'">
                    <button class="button button--disabled">Chatten är just nu full</button>
                    <p v-if="texts.queue" v-html="texts.queue"></p>
                </div>

                <div v-else>
                    <button class="button button--disabled">Chatten är stängd</button>
                    <p v-if="texts && texts.close" v-html="texts.close"></p>
                </div>

                <div v-if="texts && texts.openinghours" class="card__openinghours">
                    <label>Öppettider</label>
                    <span v-html="texts.openinghours"> </span>
                </div>
            </slot>
        </div>

        <slot v-if="currentView == 'chat' && status == 'queue'" name="queue" :queue="queue">
            <div class="chat__queue" v-if="queue.invite">
                <h2 class="chat__queue__title">{{ $t('chat.queue_title') }}</h2>
                <p class="chat__queue__text" v-html="queue.invite"></p>
            </div>
            <div class="chat__queue" v-else-if="queue.alert">
                <h2 class="chat__queue__title">{{ $t('chat.queue_title_alert') }}</h2>
                <p class="chat__queue__text" v-html="queue.alert"></p>
            </div>
            <div class="chat__queue" v-else>
                <h2 class="chat__queue__title">{{ $t('chat.queue_title') }}</h2>
                <p class="chat__queue__text" v-html="queue.message"></p>
                <br />
                <p class="chat__queue__position" v-if="queue.position == '1'" v-html="$t('chat.queue_position_first')"></p>
                <p
                    class="chat__queue__position"
                    v-else
                    v-html="
                        $t('chat.queue_position', {
                            position: '<strong>' + queue.position + '</strong>',
                        })
                    "
                ></p>
            </div>
        </slot>

        <slot v-if="currentView == 'chat' && status == 'timeout_queue'" name="queue-timeout">
            <div class="chat__queue">
                <h2 class="chat__queue__title">{{ $t('chat.queue_timeout_title') }}</h2>
                <p class="chat__queue__text">{{ $t('chat.queue_timeout_mesage') }}</p>
            </div>
        </slot>

        <slot v-if="currentView == 'chat' && (status == 'chat' || status == 'ended')" name="messges">
            <main :class="cssMessagesClass()">
                <slot name="messages-heading"> </slot>
                <div v-for="(message, index) in messages" :key="index" v-bind:class="cssMessageClass(message)">
                    <span v-if="message.source == 'joined' || message.source == 'left'" v-html="formatTime(message.date) + ' ' + message.message"></span>
                    <template v-else>
                        <span>{{ formatTime(message.date) + (message.nickname ? ' ' + message.nickname : '') }}</span>
                        <div v-html="message.message"></div>
                    </template>
                </div>
                <div v-if="typers.length > 0">
                    <div v-for="(typer, index) in typers" v-bind:key="index" class="chat__message chat__message--typing">
                        <span>
                            {{ typer.nickname }}
                        </span>
                        <div>
                            <span class="chat__message--typeing-dot"></span>
                            <span class="chat__message--typeing-dot"></span>
                            <span class="chat__message--typeing-dot"></span>
                        </div>
                    </div>
                </div>
            </main>
        </slot>

        <slot v-if="currentView == 'chat' && (status == 'chat' || status == 'ended')" name="typing" :typing="volunteerTyping" :volunteer="volunteer">
            <div class="chat__typing">
                <template v-if="volunteerTyping">{{ $t('chat.typing') }}</template>
            </div>
        </slot>

        <slot v-if="currentView == 'chat' && (status == 'chat' || status == 'ended')" name="error" :errorMessage="errorMessage">
            <div class="chat__error">
                <template v-if="errorMessage">{{ errorMessage }}</template>
            </div>
        </slot>

        <slot v-if="currentView == 'chat' && status == 'queue'" name="queue-actions">
            <section class="chat__actions">
                <div class="chat__buttons chat__buttons--one">
                    <button v-on:click="quit" class="button button--quit">
                        {{ $t('chat.btn_quit') }}
                    </button>
                </div>
            </section>
        </slot>

        <slot v-if="currentView == 'chat' && status == 'timeout_queue'" name="queue-timeout-actions">
            <section class="chat__actions">
                <div class="chat__buttons chat__buttons--one">
                    <button v-on:click="reset" class="button button--quit">
                        {{ $t('chat.btn_quit') }}
                    </button>
                </div>
            </section>
        </slot>

        <slot v-if="currentView == 'chat' && status == 'chat'" name="chat-actions">
            <section class="chat__actions">
                <textarea ref="postMessage" class="chat__postmessage" v-model="message" v-on:keyup="keyUp" tabindex="1" :placeholder="$t('chat.message_placeholder')">
                </textarea>
                <div
                    v-if="emoticons && showEmoticons !== null && !dialog"
                    class="chat__emoticons"
                    v-bind:class="{
                        'chat__emoticons--symbols': emoticonsSymbols,
                        'chat__emoticons--visible': showEmoticons,
                    }"
                    v-on:click="insertEmoticon"
                >
                    <svg style="width: 22px; height: 22px" viewBox="0 0 24 24">
                        <path
                            fill="currentColor"
                            d="M12,17.5C14.33,17.5 16.3,16.04 17.11,14H6.89C7.69,16.04 9.67,17.5 12,17.5M8.5,11A1.5,1.5 0 0,0 10,9.5A1.5,1.5 0 0,0 8.5,8A1.5,1.5 0 0,0 7,9.5A1.5,1.5 0 0,0 8.5,11M15.5,11A1.5,1.5 0 0,0 17,9.5A1.5,1.5 0 0,0 15.5,8A1.5,1.5 0 0,0 14,9.5A1.5,1.5 0 0,0 15.5,11M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"
                        />
                    </svg>
                    <span v-for="(emoticon, key) in emoticons" v-html="emoticon" :key="key" :title="key === parseInt(key, 10) ? '' : key"> </span>
                    <div class="chat__emoticons__overlay"></div>
                </div>
                <div class="chat__buttons">
                    <slot name="chat-actions-buttons" :quit="quit" :send="send">
                        <button v-on:click="quit" class="button button--link button--quit" tabindex="3" style="justify-self: start">
                            {{ $t('chat.btn_quit') }}
                        </button>
                        <button v-on:click="send" ref="send" class="button button--send" tabindex="2">
                            {{ $t('chat.btn_send') }}
                        </button>
                    </slot>
                </div>
            </section>
        </slot>

        <slot v-if="currentView == 'chat' && status == 'ended'" name="ended-actions">
            <section class="chat__actions">
                <p class="chat__ended">
                    {{ $t('chat.ended') }}
                </p>
                <div class="chat__buttons chat__buttons--one">
                    <a v-if="embed" :href="homeUrl" class="button" target="_parent">{{ $t('chat.homepage') }}</a>
                    <a v-else :href="homeUrl" class="button">{{ $t('chat.homepage') }}</a>
                </div>
            </section>
        </slot>

        <slot v-if="inlineFeedback && status == 'feedback'" name="inline-feedback" :saveFeedback="saveFeedback" :updateFeedback="updateFeedback" :end="end" :invite="invite_id"></slot>

        <slot v-if="currentView == 'chat'" name="footer" :status="status"> </slot>

        <transition name="dialog--fade">
            <div v-if="dialog == 'quit'" class="dialog__container dialog--vertically-center">
                <div class="dialog__overlay" v-on:click="dialog = null"></div>
                <slot name="dialog-quit" :quit="quit" :cancel="cancel" :texts="texts">
                    <div class="dialog">
                        <div class="dialog__text">
                            <strong>{{ $t('chat.quit_text') }}</strong>
                        </div>
                        <div class="dialog__actions">
                            <button class="button" v-on:click="quit" tabindex="11">
                                {{ $t('chat.btn_yes') }}
                            </button>
                            <button id="quitCancel" class="button" v-on:click="dialog = null" tabindex="10">
                                {{ $t('chat.btn_no') }}
                            </button>
                        </div>
                    </div>
                </slot>
            </div>
        </transition>

        <transition v-if="!inlineFeedback" name="dialog--fade">
            <div v-if="dialog == 'feedback'" class="dialog__container">
                <div class="dialog__overlay"></div>
                <slot name="feedback" :saveFeedback="saveFeedback" :updateFeedback="updateFeedback" :end="end" :invite="invite_id"> </slot>
                <div v-if="errorMessage" class="dialog__error-message">{{ errorMessage }}</div>
            </div>
        </transition>

        <transition v-if="!inlineFeedback" name="dialog--fade">
            <div v-if="dialog == 'feedback-sendt'" class="dialog__container dialog--vertically-center">
                <div class="dialog__overlay"></div>
                <slot name="feedback-sendt">
                    <div class="dialog">
                        <div class="dialog__text">
                            <strong>{{ $t('chat.feedback_sendt') }}</strong>
                        </div>
                    </div>
                </slot>
            </div>
        </transition>

        <transition name="dialog--fade">
            <div v-if="dialog == 'survey'" class="dialog__container">
                <div class="dialog__overlay"></div>
                <slot name="survey" :saveSurvey="saveSurvey" :skipSurvey="skipSurvey"> </slot>
            </div>
        </transition>

        <audio id="startSound">
            <source src="/media/start.mp3" type="audio/mpeg" />
        </audio>
    </div>
</template>

<script>
//import * as Sentry from '@sentry/browser';
const Sentry = undefined;
import { insertEmojiInTextArea } from '../utils/chat';

export default {
    name: 'Chat',

    props: {
        view: {
            type: String,
            required: true,
        },
        hasCard: {
            type: Boolean,
            default: false,
        },
        embed: {
            type: Boolean,
            default: false,
        },
        maxHeight: {
            default: null,
        },
        minHeight: {
            default: null,
        },
        sameFrame: {
            type: Boolean,
            default: true,
        },
        redirectSameFrame: {
            type: Boolean,
            default: false,
        },
        namedFrame: {
            type: String,
            default: '',
        },
        homeUrl: {
            type: String,
            default: '/',
        },
        endUrl: {
            type: String,
            default: '',
        },
        chatUrl: {
            type: String,
            default: window.location.protocol + '//' + window.location.host,
        },
        websocketUrl: {
            type: String,
            default: null,
        },
        trackState: {
            type: Boolean,
            default: false,
        },
        feedback: {
            type: Boolean,
            default: false,
        },
        inlineFeedback: {
            type: Boolean,
            default: false,
        },
        survey: {
            type: Boolean,
            default: false,
        },
        messagesClass: {
            type: String,
            default: 'bubbles',
        },
        invitePath: {
            type: String,
            default: 'invite',
        },
        groupChat: {
            type: Boolean,
            default: false,
        },
        fingerPrint: {
            type: Function,
        },
        postParentMessage: {
            type: Boolean,
            default: false,
        },
        skipResize: {
            type: Boolean,
            default: false,
        },
        typingThreshold: {
            type: Number,
            default: 3
        }
    },

    i18n: {
        messages: {
            sv: {
                chat: {
                    title: 'Chatt',

                    btn_quit: 'Avsluta',
                    btn_send: 'Skicka',
                    btn_yes: 'Ja',
                    btn_no: 'Nej',

                    btn_starting: 'Startar',

                    queue_title: 'Välkommen!',
                    queue_title_alert: 'Chatten är stängd',
                    queue_position_first: 'Du är först i kön',
                    queue_position: 'Du är nummer {position} i kön',

                    queue_timeout_title: 'Tidsavbrott',
                    queue_timeout_mesage: 'Du har varit inaktiv i mer än 5 minuter och har tappat din plats i kön.',

                    message_placeholder : 'Skriv ditt meddelande här',

                    typing: 'Volontär skriver ...',
                    ended: 'Samtalet är avslutat.',
                    homepage: 'Startsidan',

                    quit_text: 'Vill du verkligen avsluta?',
                    feedback_sendt: 'Tack för din återkoppling!',

                    send_error: 'Det gick inte att skicka. Försök igen.',
                    send_closed: 'Det gick inte att skicka. Samtalet är avslutat.',
                },
            },
            nb: {
                chat: {
                    title: 'Chat',

                    btn_quit: 'Avslutt',
                    btn_send: 'Send',
                    btn_yes: 'Ja',
                    btn_no: 'Nei',

                    btn_starting: 'Starter',

                    queue_title: 'Velkommen!',
                    queue_title_alert: 'Chatten er stengt',
                    queue_position_first: 'Du er først i køen',
                    queue_position: 'Du er nummer {position} i køen',

                    queue_timeout_title: 'Tidsavbrudd',
                    queue_timeout_mesage: 'Du har vært inaktiv i mer enn 5 minutter og har mistet din plass i køen.',

                    message_placeholder : 'Skriv din melding her',

                    typing: 'Frivillig skriver ...',
                    ended: 'Samtalen er avsluttet.',
                    homepage: 'Forsiden',

                    quit_text: 'Vil du avslutte samtalen?',
                    feedback_sendt: 'Takk for din tilbakemelding',

                    send_error: 'Kunne ikke sende. Prøv igjen.',
                    send_closed: 'Kunne ikke sende. Chatten er avsluttet.',
                },
            },
        },
    },

    data() {
        return {
            accessToken : null,

            currentView: this.view,

            starting: false,
            ready: false,
            id: null,
            status: '',
            messages: [],
            message: '',
            typing: null,
            typingInterval: null,

            typers: [],
            typersIdleInterval: null,

            queue: {},
            invite_id: null,

            volunteer: '',
            volunteerTyping: false,

            texts: {},
            emoticons: null,
            emoticonsSymbols: false,
            showEmoticons: false,
            site: {},
            invites: [],

            dialog: null,

            connected: false,
            pendingConnect: false,
            websocket: null,
            websocketStatusInterval: null,
            token: null,
            userId: null,

            fingerPrintId: null,

            poll: {
                lastTimestamp: null,
                read: false,
                timeout: 10000,
            },
            readInterval: null,

            errorMessage: null,

            inIframe: false,
            width: 0,
            height: 0,
            fullscreen: false,

            debug: process.env.NODE_ENV == 'development',
        };
    },
    computed: {
        sameDomain: function () {
            return this.chatUrl == window.location.protocol + '//' + window.location.host;
        },
    },
    watch: {
        status: function (newStatus, currentStatus) {
            if (currentStatus) {
                this.log('status ' + currentStatus + ' => ' + newStatus);
            } else {
                this.log('status ' + newStatus);
            }

            if (!this.websocketUrl) {
                if (!this.readInterval) {
                    if (newStatus == 'queue' || newStatus == 'chat') {
                        this.scheduleRead();
                    }
                }
                if (newStatus == 'queue' && this.survey) {
                    window.setTimeout(() => {
                        this.dialog = 'survey';
                    }, 2000);
                }
            }

            // post message to parent window
            if (this.postParentMessage && window.self !== window.top) {
                const message = JSON.stringify({
                    origin: 'noop chat',
                    state: newStatus,
                    lastState: currentStatus,
                });
                parent.postMessage(message, '*');
            }

            if(this.embed) {
                window.setTimeout(() => {
                    this.resize();
                }, 200);
            }
        },
        maxHeight: function (/*value*/) {
            this.resize();
        },
        minHeight: function (/*value*/) {
            this.resize();
        },
    },

    /***
     * created
     */
    created: function () {
        try {
            this.inIframe = window.self !== window.top;
        } catch (e) {
            this.inIframe = false;
        }

        if (this.view == 'card' && !this.inIframe && this.redirectSameFrame) {
            if (this.homeUrl) {
                window.location = this.homeUrl;
                return;
            } else {
                document.body.classList.add('noframe');
            }
        }

        if (this.embed) {
            /*document.body.style.height = "auto";
      document.documentElement.style.height = "auto";*/
        } else if (this.currentView == 'chat') {
            document.body.classList.add('chat');
        }

        if (this.trackState) {
            document.addEventListener('visibilitychange', this.handleVisibilityChange);
        }

        if (this.embed) {
            if (!this.skipResize) {
                document.addEventListener('resize', this.resize);
            }
        }

        if (this.groupChat) {
            this.typersIdleInterval = window.setInterval(() => {
                this.idleTypers();
            }, 1000);
        }

        this.initialize();
    },

    /***
     * destroyed
     */
    destroyed: function () {
        if (this.readInterval) {
            clearTimeout(this.readInterval);
            this.readInterval = null;
        }

        if (this.typersIdleInterval) {
            window.clearInterval(this.typersIdleInterval);
            this.typersIdleInterval = null;
        }

        if (this.trackState) {
            document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        }

        if (this.embed) {
            if (!this.skipResize) {
                document.removeEventListener('resize', this.resize);
            }
        }
    },

    methods: {
        insertEmojiInTextArea,
        /**
         * resize
         */
        resize: function (params = null) {
            if (this.ready) {
                if (params) {
                    const width = params.width;
                    const height = params.height;
                    if (window.self !== window.top) {
                        const message = JSON.stringify({
                            width: width,
                            height: height,
                            origin: 'noop chat',
                            state: this.status,
                        });
                        this.log('resize: ' + message);
                        parent.postMessage(message, '*');
                    }
                } else {
                    // get first div
                    let element = document.body;
                    for (const child of document.body.children) {
                        if (child.nodeName == 'DIV') {
                            element = child;
                            break;
                        }
                    }

                    // calculate widh and height
                    const style = window.getComputedStyle ? getComputedStyle(element, null) : element.currentStyle;
                    const width = Math.max(parseInt(style.width), element.scrollWidth, element.offsetWidth); /* +
                    (parseInt(style.marginLeft) || 0) +
                    (parseInt(style.marginRight) || 0)*/

                    let height = Math.max(parseInt(style.height), element.scrollHeight, element.offsetHeight); /* +
                    (parseInt(style.marginTop) || 0) +
                    (parseInt(style.marginBottom) || 0)*/

                    if (this.maxHeight > 0) {
                        height = Math.min(this.maxHeight, height);
                    }
                    if (this.minHeight > 0) {
                        height = Math.max(this.minHeight, height);
                    }
                    //this.fullscreen = false;

                    // if changed
                    if (this.height != height || this.width != width) {
                        this.height = height;
                        this.width = width;

                        // post message to parent window
                        if (window.self !== window.top) {
                            const message = JSON.stringify({
                                width: width,
                                height: height,
                                origin: 'noop chat',
                                state: this.status,
                            });
                            this.log('resize: ' + message);

                            parent.postMessage(message, '*');

                            /*window.setTimeout(() => {
                            if (window.screen.width == document.body.clientWidth && window.screen.height - document.body.clientHeight < 50) {
                                this.fullscreen = true;
                            } else {
                                this.fullscreen = false;
                            }
                        }, 500);*/
                        }
                    }
                }
            }
        },

        /**
         * start
         */
        start: async function (event, invite = null, params = null, gtag = false) {
            // track: queue
            if (gtag === false && typeof this.$gtag == 'object') {
                this.log('event queue');
                let started = false;

                try {
                    this.$gtag.event('Queue', {
                        event_category: 'Chat',
                        event_callback: () => {
                            this.log('tracked');
                            if (!started) {
                                started = true;
                                this.start(event, invite, params, true);
                            }
                        },
                    });
                } catch (e) {
                    this.log('track error');
                    this.log(e);
                } finally {
                    window.setTimeout(() => {
                        if (!started) {
                            started = true;
                            this.start(event, invite, params, true);
                        }
                    }, 400);
                }
                return;
            }

            if (this.starting) {
                return false;
            }
            this.starting = true;

            if (event && event.target && event.target.tagName == 'BUTTON') {
                const button = event.target;
                let start = this.$i18n.t('chat.btn_starting');
                if (start) {
                    button.innerHTML = start;
                    let dots = 1;
                    let interval = window.setInterval(() => {
                        start += '.';
                        button.innerHTML = start;
                        if (dots++ > 3) {
                            window.clearInterval(interval);
                        }
                    }, 500);
                }
            }

            if (this.isWebSocket()) {
                /*this._connect((websocket) => websocket.send('{"type":"queue"}'));*/
                await this.connect(true, params);
            } else {
                const uri = invite ? '/chat/' + this.invitePath + '/' + invite : '/chat/start';
                let data = typeof params == 'object' ? params : typeof params == 'string' ? { alias: params } : null;

                if (this.fingerPrintId) {
                    if (!data) {
                        data = {};
                    }
                    data.fpid = this.fingerPrintId;
                }

                let query = '';
                if (data) {
                    query = '?';
                    for (const variable in data) {
                        if (data[variable]) {
                            const value = encodeURIComponent(data[variable]);
                            query += variable + '=' + value + '&';
                        } else {
                            delete data[variable];
                        }
                    }
                    query = query.slice(0, -1);
                }

                if (!this.sameFrame && this.inIframe) {
                    window.top.location.href = this.chatUrl + uri + query;
                } else if (!this.embed && !this.sameDomain) {
                    window.location.href = this.chatUrl + uri + query;
                } else {
                    const response = await this.request({
                        method: 'post',
                        url: this.chatUrl + uri,
                        data: data,
                    });

                    if (response.data) {
                        const message = response.data;
                        if (typeof message.type != 'undefined' && message.type == 'start') {
                            this.goto();
                        }
                    }
                }
            }
        },

        /***
         * initialize
         */
        initialize: async function () {
            if (this.fingerPrint) {
                try {
                    if (this.fingerPrint) {
                        this.fingerPrintId = await this.fingerPrint();
                    }
                } catch (e) {
                    this.exception(e);
                }
            }

            try {
                const response = await this.request({
                    method: 'get',
                    url: this.chatUrl + '/chat/state' + (this.fingerPrintId ? '/' + this.fingerPrintId : ''),
                });
                if (typeof response.data === 'object') {
                    // reintitialize
                    if (this.readInterval) {
                        clearTimeout(this.readInterval);
                        this.readInterval = null;
                    }

                    const state = response.data;

                    this.status = state.status;

                    if (state.status == 'closed') {
                        this.status = state.chat_state == 'close' ? 'closed' : state.chat_state;
                    }

                    this.texts = state.texts;
                    const emoticons = state.emoticons || null;
                    if (emoticons) {
                        let list = null;
                        this.emoticons = {};
                        let last = null;
                        for (let key in emoticons) {
                            if (key) {
                                if (emoticons[key] != last) {
                                    this.emoticons[key] = emoticons[key];
                                }
                                last = emoticons[key];
                            } else {
                                const characters = emoticons[key].split(/([\uD800-\uDBFF][\uDC00-\uDFFF])/);
                                list = [];
                                for (var i = 0; i < characters.length; i++) {
                                    const char = characters[i];
                                    if (char !== '') {
                                        list.push(char);
                                    }
                                }
                            }
                        }
                        Message.emoticons = this.emoticons;
                        if (list) {
                            this.emoticons = list;
                            this.emoticonsSymbols = true;
                        }

                        if (typeof state.emoticons_button == 'boolean' && !state.emoticons_button) {
                            this.showEmoticons = null;
                        }
                    }

                    this.site = state.site || {};
                    this.invites = state.invites ? state.invites : null;

                    this.lastTimestamp = state.message_last_timestamp;

                    this.id = null;
                    this.messages = [];

                    if (state.chat) {
                        this.id = state.chat.id;
                        this.invite_id = state.chat.invite_id;

                        if (state.chat.messages) {
                            for (const message of state.chat.messages) {
                                if (message.message) {
                                    this.messages.push(new Message(message.type, message.message, message.date, !message.volunteer));
                                }
                            }
                        }

                        this.volunteer = state.chat.volunteer || '';
                    } else if (state.queue) {
                        this.queue = Object.assign(state.queue, { text: this.texts.queue });
                        this.invite_id = this.queue.invite_id;
                    }

                    if (this.isWebSocket()) {
                        this.token = state.token;
                        if (this.token) {
                            await this.connect(false);
                        }
                    }

                    this.ready = true;
                    this.poll.read = true;
                    this.poll.lastTimestamp = this.lastTimestamp;

                    this.scrollToEnd();

                    if (this.embed) {
                        if (this.status == 'queue' || this.status == 'chat') {
                            this.currentView = 'chat';
                            /*document.body.style.removeProperty("height");
                              document.documentElement.style.removeProperty("height");*/
                        }

                        if (!this.skipResize) {
                            window.setTimeout(() => {
                                this.resize();
                            }, 100);
                        }
                    }
                    this.$emit('initialize', {
                        id: this.id,
                        view: this.currentView,
                        state: this.state,
                        status: this.status,
                        site: this.site,
                        texts: this.texts,
                    });
                    if (typeof Sentry != 'undefined') {
                        if (this.id) {
                            Sentry.setUser({ id: this.id });
                        }
                    }

                    // track: visit
                    if (typeof this.$gtag == 'object') {
                        this.log('event visit');
                        this.$gtag.event('Visit', { event_category: 'Chat' });
                    }
                } else if (response.data && response.data.errors) {
                    this.log(response.data.errors);
                }

            } catch (e) {
                this.exception(e);
            }
        },

        /**
         * connect
         */
        isWebSocket: function () {
            return this.websocketUrl ? true : false;
        },

        /**
         * connect
         */
        connect: async function (start = true, params = {}) {
            if (!this.websocketUrl) {
                return;
            }

            const nickname = typeof params == 'string' ? params : '';

            if (start) {
                start = () => {
                    if (this.groupChat) {
                        this.websocket.send(
                            JSON.stringify({
                                type: 'join',
                                nickname: nickname,
                            })
                        );
                    }
                };
            }

            if (this.connected && !this.pendingConnect) {
                if (start) {
                    start();
                }
                return;
            }

            if (!this.token) {
                const response = await this.request({
                    method: 'post',
                    url: this.chatUrl + '/chat/token',
                    data: {},
                });

                if (response && response.data && response.data.token) {
                    this.token = response.data.token;
                }
            }

            if (this.token) {
                this.log('websocket connect: ' + this.token);
                this.pendingConnect = false;
                this.connected = null;

                const query = '?token=' + this.token;
                this.websocket = new WebSocket(this.websocketUrl + query);
                this.websocket.addEventListener('open', (/*event*/) => {
                    this.log('websocket connected');
                    this.connected = true;

                    this.websocket.addEventListener('message', (event) => {
                        this.handleMessage(event);
                    });

                    this.websocketStatusInterval = setInterval(() => {
                        this.updateSocketStatus();
                    }, 1000);

                    this.websocket.addEventListener('error', (event) => {
                        this.log('WebSocket error');
                        this.log(event);
                    });

                    if (start) {
                        start();
                    }

                    return true;
                });

                setTimeout(() => {
                    if (this.connected == null) {
                        this.connected = false;
                    }
                }, 1000);
            }
        },

        /**
         * disconnect
         */
        disconnect: function (reconnect = false) {
            this.pendingConnect = true;
            if (this.websocketStatusInterval) {
                clearInterval(this.websocketStatusInterval);
                this.websocketStatusInterval = null;
            }
            if (this.websocket) {
                if (this.websocket.readyState > 1) {
                    this.websocket.close();
                }
                this.websocket = null;
            }

            this.log('Websocket disconnected');

            if (reconnect) {
                this.log('Websocket wait reconnect');
                setTimeout(() => {
                    if (this.pendingConnect) {
                        this.log('Websocket reconnect');
                        this.connect();
                    }
                }, 1000);
            } else {
                this.pendingConnect = false;
                this.connected = false;
            }
        },

        /**
         * updateSocketStatus
         */
        updateSocketStatus: function () {
            if (this.connected && !this.pendingConnect && this.websocket && this.websocket.readyState != 1) {
                this.log('Websocket disconnect');
                this.disconnect(true);
            }
        },

        /**
         * goto
         */
        goto: function () {
            if (this.namedFrame && window.name != this.namedFrame) {
                window.open(this.chatUrl + '/chat', this.namedFrame);
                this.initialize();
            } else if (!this.sameFrame && this.inIframe) {
                window.top.location.href = this.chatUrl + '/chat';
            } else if (!this.embed && !this.sameDomain) {
                window.location.href = this.chatUrl + '/chat';
            } else if (this.embed || this.view == 'chat') {
                this.initialize();
            } else {
                this.$router.push({ name: 'Chat' });
            }
        },

        /**
         * reset
         */
        reset: function () {
            if (this.namedFrame && window.name == this.namedFrame) {
                try {
                    window.close();
                    return;
                } catch (e) {
                    this.log(e);
                }
            }
            if (this.embed) {
                this.currentView = 'chat';
                this.initialize();
                this.resize();
            } else if (this.currentView != 'chat') {
                this.initialize();
            } else if (this.hasCard && this.view == 'chat') {
                this.initialize();
            } else if (this.debug || this.homeUrl == '/') {
                let homeUrl = this.debug ? '/' : this.homeUrl;
                if (this.$router.currentRoute.fullPath == this.homeUrl) {
                    location.reload();
                } else {
                    this.$router.push({ path: homeUrl });
                }
            } else {
                window.location = this.homeUrl;
            }
        },

        /**
         * keyUp
         */
        keyUp: async function (e) {
            // send on enter
            if (e.keyCode === 13) {
                if (!e.shiftKey) {
                    const message = e.target;
                    try {
                        if (this.message && message.selectionStart == message.selectionEnd && message.selectionStart == message.value.length) {
                            this.send();
                        }
                    } catch (e) {
                        this.exception(e);
                    }
                } else {
                    // assert soft newline with shift key down
                    /*if(this.message && !(/\n$/.test(this.message)))
                        this.message += "\n";*/
                }
            } else {
                const message = e.target;
                if (message.value.length >= this.typingThreshold) {
                    this.setTyping(true);
                } else if (!message.value.length) {
                    this.setTyping(false);
                }
            }
        },

        /**
         * idleTypers
         */
        idleTypers: function () {
            if (this.typers.length) {
                this.typers = this.typers.filter((typer) => {
                    return new Date().getTime() - typer.typed < 7000;
                });
            }

            // idle self
            if (this.typing && new Date().getTime() - this.typing >= 3000) {
                this.setTyping(false);
            }
        },

        /**
         * insertEmoticon
         */
        insertEmoticon: function (e) {
            if (e.target.tagName == 'DIV') {
                this.showEmoticons = false;
            } else if (!this.showEmoticons) {
                this.showEmoticons = true;
            } else if (e.target.tagName == 'SPAN') {
                const element = e.target;
                let emoticon = element.getAttribute('title');
                if (!emoticon) {
                    emoticon = element.innerText;
                }

                this.showEmoticons = false;
                const textarea = this.$refs.postMessage;
                this.message = this.insertEmojiInTextArea(emoticon, textarea);
            }
        },

        /**
         * send
         */
        send: async function () {
            if (this.message && this.message.replace(/(\n|\s)/g, '')) {
                let sent = false;
                const message = this.message;
                this.message = '';

                // done typing
                this.setTyping(null);

                try {
                    if (this.isWebSocket()) {
                        await this.websocket.send(
                            JSON.stringify({
                                type: 'message',
                                id: this.id,
                                message: message,
                            })
                        );
                        sent = true;
                    } else {
                        // send message
                        const response = await this.request({
                            method: 'post',
                            url: this.chatUrl + '/chat/send',
                            data: { message: message },
                        });

                        if (response.data) {
                            if (response.data.type == 'sent') {
                                const sentMessage = Object.assign({ message: message }, response.data);
                                this.log(sentMessage);

                                this.addMessage(new Message('sent', sentMessage.message, sentMessage.date, true));

                                sent = true;
                            } else if (response.data.type == 'error') {
                                // error
                                const error_code = response.data.error_code;
                                this.volunteerTyping = false;
                                this.errorMessage = this.$i18n.t(error_code == 'chat_closed' ? 'chat.send_closed' : 'chat.send_error');
                                setTimeout(() => {
                                    this.errorMessage = null;

                                    if(error_code === 'chat_closed') {
                                        this.quit(null, null, true);
                                    }
                                }, 5000);
                            }
                        }
                    }

                    // on mobile, focus send  button to hide keyboard
                    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
                        window.setTimeout(() => {
                            this.$refs.send.focus();
                        }, 100);
                    }
                } catch (e) {
                    this.exception(e);
                } finally {
                    if (!sent) {
                        this.message = message;
                        if(!this.errorMessage) {
                            this.volunteerTyping = false;
                            this.errorMessage = this.$i18n.t('chat.send_error');
                            setTimeout(() => {
                                this.errorMessage = null;
                            }, 5000);
                        }
                    }
                }
            }
        },

        /**
         * setTyping
         */
        setTyping: async function (typing) {
            const changed = (this.typing == null && typing) || (this.typing != null && typing == false);

            this.typing = typing ? new Date().getTime() : null;

            if (changed) {
                const type = typing ? 'typing' : 'idle';

                // send typing/idle message
                try {
                    if (this.isWebSocket()) {
                        await this.websocket.send(
                            JSON.stringify({
                                type: type,
                                id: this.id,
                            })
                        );
                        return true;
                    } else {
                        const response = await this.request({
                            method: 'get',
                            url: this.chatUrl + '/chat/' + type,
                        });
                        if (response.data) {
                            const sentMessage = Object.assign({ type: type }, response.data);
                            this.log(sentMessage);
                        }
                    }
                } catch (e) {
                    this.log(e);
                }
            }
        },

        /**
         * track
         */
        track: async function (state) {
            try {
                if (this.status == 'chat') {
                    // send state
                    await this.request({
                        method: 'get',
                        url: this.chatUrl + '/chat/track/' + state,
                    });
                }
            } catch (e) {
                this.log(e);
            }
        },

        /**
         * addMessage
         */
        addMessage: function (message) {
            this.messages.push(message);

            this.scrollToEnd();

            /*if (typeof(notification_sound) != "undefined" && notification_sound) {
                playSound("message");
            }*/
        },

        /**
         * scrollToEnd
         */
        scrollToEnd: function () {
            window.setTimeout(() => {
                const messages = document.getElementsByClassName('chat__messages')[0];
                if (messages) {
                    messages.scrollTop = messages.scrollHeight;
                }
            }, 200);
        },

        /**
         * quit
         */
        quit: async function (event = null, gtag = false, force = false) {
            if (this.currentView == 'chat' && this.dialog != 'quit' && !force) {
                this.dialog = 'quit';
                window.setTimeout(() => {
                    const cancel = document.getElementById('quitCancel');
                    if (cancel) {
                        cancel.focus();
                    }
                }, 200);
            } else {
                // track: quit
                if (gtag === false && typeof this.$gtag == 'object') {
                    this.log('event quit');
                    try {
                        this.$gtag.event('Queue', {
                            event_category: 'Chat',
                            event_callback: () => {
                                this.quit(event, true);
                            },
                        });
                    } catch (e) {
                        this.log('quit error');
                        this.log(e);
                    } finally {
                        window.setTimeout(() => {
                            if (this.status != 'ended') {
                                this.quit(event, true);
                            }
                        }, 200);
                    }
                    return;
                }

                const status = this.status;
                this.dialog = '';

                if (this.isWebSocket()) {
                    await this.websocket.send(
                        JSON.stringify({
                            type: 'exit',
                        })
                    );

                    if (this.inlineFeedback) {
                        this.status = 'feedback';
                    } else if (this.feedback) {
                        this.status = 'ended';
                        this.dialog = 'feedback';
                    } else {
                        this.reset();
                    }
                } else {
                    // send
                    const response = await this.request({
                        method: 'get',
                        url: this.chatUrl + '/chat/quit',
                    });

                    if (response.data) {
                        if (response.data.result == 'success') {
                            this.$emit('ended');

                            if (status == 'chat' && this.inlineFeedback) {
                                this.status = 'feedback';
                            } else if (status == 'chat' && this.feedback) {
                                this.status = 'ended';
                                this.dialog = 'feedback';
                            } else {

                                this.$emit('end', {
                                    id: this.id,
                                    status: status
                                });

                                this.reset();
                            }
                        }
                    }
                }
            }
        },

        /**
         * leave
         */
        /* eslint-disable no-unused-vars */
        leave: async function (event = null, url = 'about:blank') {
            // send
            await this.request({
                method: 'get',
                url: this.chatUrl + '/chat/quit',
            });

            window.location.replace(url);

            this.reset();
        },

        /**
         * end
         */
        end: function () {
            this.status = 'ended'
            if(this.endUrl || this.homeUrl) {
                window.setTimeout(() => {
                    if (this.embed) {
                        location.reload();
                    } else if (this.debug || this.homeUrl == '/') {
                        if (this.$router.currentRoute.fullPath == '/') {
                            location.reload();
                        } else {
                            this.$router.push({ path: '/' });
                        }
                    } else {
                        window.location = this.endUrl ? this.endUrl : this.homeUrl;
                    }
                }, 1000);
            } else {
                this.$emit('end', {
                    id: this.id,
                });
            }
        },

        cancel: function () {
            this.dialog = false;
        },

        /**
         * saveFeedback
         */
        saveFeedback: async function (feedback) {
            this.log('saveFeedback');

            feedback.chat_id = this.id || null;
            this.log(feedback);

            // save
            if (this.isWebSocket()) {
                console.log('TODO');
            } else {
                const response = await this.request({
                    method: 'post',
                    url: this.chatUrl + '/chat/feedback',
                    data: feedback,
                });

                // keep the dialog open on a failed save so the answers are not lost
                if (!response.data || response.data.result != 'success') {
                    this.setErrorMessage(this.$i18n.t('chat.send_error'));
                    return;
                }

                this.log(response.data);
            }

            this.dialog = 'feedback-sendt';
            window.setTimeout(() => {
                this.dialog = '';
                this.end();
            }, 1000);
        },

        /**
         * updateFeedback
         */
        updateFeedback: async function (feedback) {

            this.log('updateFeedback');

            feedback.chat_id = this.id || null;
            this.log(feedback);

            // save
            if (this.isWebSocket()) {
                console.log('TODO');
            } else {
                const response = await this.request({
                    method: 'post',
                    url: this.chatUrl + '/chat/feedback',
                    data: feedback,
                });

                if (response.data) {
                    const result = response.data;
                    this.log(result);
                }
            }
        },

        /**
         * saveSurvey
         */
        saveSurvey: async function (survey) {
            this.log('saveSurvey');
            this.log(survey);

            // save
            const response = await this.request({
                method: 'post',
                url: this.chatUrl + '/chat/survey',
                data: survey,
            });

            if (response.data) {
                const result = response.data;
                this.log(result);
            }

            window.setTimeout(() => {
                this.dialog = '';
            }, 2000);
        },

        /**
         * skipSurvey
         */
        skipSurvey: async function () {
            this.dialog = '';
        },

        /***
         * addNotification
         */
        addNotification: async function (notification, type = null) {
            let now = new Date();
            let date = new Date(now.getTime() - now.getTimezoneOffset() * 60 * 1000);

            const message = {
                type: type ? type : 'notification',
                message: notification,
                date: date.toISOString().split('T')[0] + ' ' + date.toISOString().split('T')[1].substring(0, 5),
            };

            this.addMessage(new Message(message.type, message.message, message.date, false));
        },

        /***
         * setErrorMessage
         */
        setErrorMessage: async function (message, timeout = 5000) {
            this.errorMessage = message;
            setTimeout(() => {
                this.errorMessage = null;
            }, timeout);
        },

        /***
         * read
         */
        scheduleRead: function () {
            this.readInterval = setTimeout(() => this.read(), 2000);
        },

        read: async function () {
            if (!this.poll.read) {
                this.scheduleRead();
                return;
            }

            try {
                const response = await this.request({
                    method: 'get',
                    url: this.chatUrl + '/chat/read?last=' + this.poll.lastTimestamp,
                });

                if (response.data) {
                    let lastTimestamp = null;
                    const messages = Array.isArray(response.data) ? response.data : Array(response.data);
                    for (const message of messages) {
                        if (message.time) {
                            lastTimestamp = message.time;
                        }

                        if (message.type) {
                            this.log(message);

                            switch (message.type) {
                                case 'queue':
                                    this.queue.position = message.position;
                                    this.queue.alert = null;
                                    break;

                                case 'queue-message':
                                    this.queue.position = message.position;
                                    this.queue.alert = message.alert;
                                    break;

                                case 'queue-timeout':
                                    this.status = 'timeout_queue';
                                    break;

                                case 'chat':
                                    if (message.message) {
                                        this.messages.push(new Message('created', message.message, message.date, message.volunteer));
                                    }
                                    this.volunteer = message.volunteer || '';
                                    this.status = 'chat';
                                    this.id = message.id;

                                    this.playSound('start');

                                    if (this.trackState) {
                                        this.track('start');
                                    }

                                    // track: start
                                    if (typeof this.$gtag == 'object') {
                                        this.log('event start');
                                        this.$gtag.event('Start', { event_category: 'Chat' });
                                    }
                                    break;

                                case 'finished':
                                    this.status = 'ended';
                                    this.$emit('ended');

                                    if (this.inlineFeedback) {
                                        this.status = 'feedback';
                                    } else if (this.feedback) {
                                        window.setTimeout(() => {
                                            this.dialog = 'feedback';
                                        }, 500);
                                    }
                                    break;

                                case 'message':
                                    this.addMessage(new Message(message.type, message.message, message.date, false));
                                    this.volunteerTyping = false;
                                    break;

                                case 'notification':
                                    this.addMessage(new Message(message.type, message.message, message.date, false));
                                    break;

                                case 'typing':
                                    this.volunteerTyping = true;
                                    break;

                                case 'idle':
                                    this.volunteerTyping = false;
                                    break;

                                case 'video-start':
                                case 'video-join':
                                case 'video-stop':
                                case 'audio-start':
                                case 'audio-join':
                                case 'audio-stop':
                                    this.addMessage(new Message(message.type, message.message, message.date, false));
                                    break;

                                case 'transfer':
                                    var code = message.message;
                                    this.log('transfer ' + code);
                                    this.start(null, code);
                                    break;

                                /*
                                case 'queue-timeout':
                                    $("div#blocked").find(".expired").show().prev().hide();
                                    updateState("blocked", true, true);
                                    break;

                                case 'expired':
                                    $("div#blocked").find(".expired").show().prev().hide();
                                    updateState("blocked", true, true);
                                    break;
                                */
                            }

                            // notify
                            this.$emit('message', message);
                        }
                    }

                    if (lastTimestamp) {
                        this.poll.lastTimestamp = lastTimestamp;
                    }
                }
            } catch (e) {
                this.exception(e);
            }

            // check idle typing
            if (this.typing && new Date().getTime() - this.typing >= 2000) {
                this.setTyping(false);
            }

            this.scheduleRead();
        },

        /**
         * handleVisibilityChange
         */
        handleVisibilityChange: function () {
            const visible = document.visibilityState === 'visible';
            if (this.trackState) {
                this.track(visible ? 'visible' : 'hidden');
            }
            /*if (this.isWebSocket()) {
                this.websocket.send(
                    JSON.stringify({
                        type: visible ? 'active' : 'inactive',
                    })
                );
            }*/
        },

        /**
         * websocket messages
         */
        handleMessage: function (event) {
            let message = null;
            try {
                message = JSON.parse(event.data);
            } catch (e) {
                this.log('invalid message');
                this.log(e);
            }

            if (message && message.type) {
                this.log(message);
                switch (message.type) {
                    case 'user':
                        this.userId = message.id;
                        break;

                    case 'queue':
                        this.queue = { position: 1, text: '', alert: '' };
                        this.status = 'queue';
                        break;

                    case 'chat':
                    case 'groupchat':
                        {
                            this.messages = [];
                            const chat = message;

                            if (chat.messages) {
                                for (const message of message.messages) {
                                    this.messages.push(
                                        new Message(
                                            message.type,
                                            message.message || '',
                                            message.sentAt,
                                            message.userId == this.userId,
                                            message.nickname,
                                            message.color
                                        )
                                    );
                                }
                            }

                            this.volunteer = '';
                            this.id = chat.id;
                            this.status = chat.isEnded ? 'ended' : 'chat';

                            this.playSound('start');
                            this.scrollToEnd();
                        }
                        break;

                    case 'message':
                        this.addMessage(
                            new Message(message.type, message.message, message.sentAt, message.userId == this.userId, message.nickname, message.color)
                        );
                        this.volunteerTyping = false;

                        message = Object.assign({}, message);
                        message.type = 'idle';

                    // eslint-disable-next-line no-fallthrough
                    case 'typing':
                    case 'idle':
                        {
                            const id = message.userId || message.volonteerId;
                            const nickname = message.nickname;
                            if (id) {
                                const typers = this.typers;
                                let typer = null;
                                for (const i in typers) {
                                    if (typers[i].id == id) {
                                        typer = typers[i];
                                        if (message.type == 'idle') {
                                            typers.splice(i, 1);
                                            break;
                                        }
                                    }
                                }
                                if (message.type == 'typing' && !typer) {
                                    typers.push({ id: id, nickname: nickname, typed: new Date().getTime() });
                                    this.scrollToEnd();
                                }
                            }
                        }
                        break;

                    case 'notification':
                        this.addMessage(new Message(message.type, message.message, message.sentAt, false));
                        break;

                    case 'left':
                    case 'joined':
                        this.addMessage(new Message(message.type, message.message, message.sentAt, message.userId == this.userId, message.nickname));
                        break;

                    case 'ended':
                        this.status = 'ended';
                        break;

                    /*case 'queue':
                        if (this.status != 'queue') {
                            this.queue = {
                                position: message.queue,
                                text: message.text,
                            };
                            this.texts.queue = message.text;
                            this.status = 'queue';
                        } else {
                            this.queue.position = message.queue;
                        }
                        break;*/

                    default:
                        console.log('unhandled message:' + message.type);
                }
            }
        },

        /**
         * formatTime
         */
        formatTime: function (value) {
            return value.substr(11, 5);
        },

        /**
         * playSound
         */
        playSound: function (sound) {
            try {
                const context = new AudioContext();
                if (context.state == 'running') {
                    const audio = document.getElementById(sound + 'Sound');
                    if (audio) {
                        audio.play();
                    }
                }
            } catch (e) {
                this.log(e);
            }
        },

        /**
         * request
         */
        request: async function(payload) {
            try {
                // use bearer token
                if(this.accessToken) {
                    if(!payload.headers) {
                        payload.headers = { Authorization: 'Bearer ' + this.accessToken };
                    } else {
                        payload.headers.Authorization = 'Bearer ' + this.accessToken;
                    }
                }

                const response = await this.$http(payload);

                // check for new token in response
                if(response && response.headers && response.headers.authorization && (response.headers.authorization.indexOf('Bearer ') == 0)) {
                    this.accessToken = response.headers.authorization.substring(7);
                }

                return response;
            } catch(e) {
                this.log(e.message);
            }

            return {};
        },

        /**
         * exception
         */
        exception: function (error) {
            this.log(error);

            if (typeof Sentry != 'undefined' && process.env.NODE_ENV === 'production') {
                if (error.message.match(/Network Error/)) {
                    Sentry.withScope((scope) => {
                        scope.setLevel(Sentry.Severity.Info);
                        Sentry.captureException(error);
                    });
                } else {
                    Sentry.captureException(error);
                }
            }
        },

        /**
         * cssMessagesClass
         */
        cssMessagesClass: function () {
            const classes = { chat__messages: true };
            if (this.messagesClass) {
                classes['chat__messages--' + this.messagesClass] = true;
            }
            return classes;
        },

        /**
         * cssMessageClass
         */
        cssMessageClass: function (message) {
            const classes = {
                chat__message: true,
                'chat__message--welcome': message.source == 'created',
                'chat__message--me': message.source == 'me',
                'chat__message--volunteer': message.source == 'volunteer',
                'chat__message--notification':
                    message.source == 'joined' ||
                    message.source == 'left' ||
                    message.source == 'video-start' ||
                    message.source == 'video-join' ||
                    message.source == 'video-stop' ||
                    message.source == 'audio-start' ||
                    message.source == 'audio-join' ||
                    message.source == 'audio-stop',
            };
            if (message.color) {
                classes['chat__message--color-' + message.color] = true;
            }
            return classes;
        },

        /**
         * log
         */
        log: function (message) {
            if (this.debug) {
                window.console.log(message);
            }
        },
    },
};

/**
 * Message
 */
class Message {
    static emoticons = null;
    static emoticonPattern = null;
    static emoticonUnicodeSet = null;

    source;
    date;
    message;
    nickname;
    color;

    constructor(type, message, date, me, nickname, color) {
        this.type = type;

        if (type == 'message') {
            this.source = me ? 'me' : 'volunteer';
        } else if (type == 'sent') {
            this.source = 'me';
        } else {
            this.source = type;
        }

        // Emoticons FIRST (before HTML escaping) — replace with plain Unicode characters
        // This handles both raw input (<3) and pre-escaped input from PHP (&lt;3)
        if (Message.emoticons) {
            if (Message.emoticonPattern == null) {
                let emoticonPattern = [];
                for (let e in Message.emoticons) {
                    emoticonPattern.push(e.replace(/[-[\]{}()*+?.,\\^$|#:\s]/g, '\\$&'));
                }
                Message.emoticonPattern = new RegExp(emoticonPattern.join('|'), 'g');
                Message.emoticonUnicodeSet = [...new Set(Object.values(Message.emoticons))];
            }

            message = message.replace(Message.emoticonPattern, function (sym) {
                return Message.emoticons[sym] || sym;
            });
        }

        if (type != 'created') {
            const escapeHtml = function (text) {
                return text
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;');
            };

            // Preserve <a> tags before escaping — these come from admin-configured
            // message templates (standard messages with booking/info links). Never
            // preserve anchors in the caller's own messages, and rebuild preserved
            // anchors from a validated href only, so no other attributes survive.
            const anchorPlaceholders = [];
            if (this.source !== 'me') {
                message = message.replace(/<a\s[^>]*href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, function (match, href, label) {
                    if (!/^https?:\/\//i.test(href)) {
                        return match;
                    }
                    const placeholder = '__ANCHOR_' + anchorPlaceholders.length + '__';
                    anchorPlaceholders.push(
                        '<a href="' + escapeHtml(href) + '" target="_blank" rel="noopener noreferrer">' + escapeHtml(label) + '</a>'
                    );
                    return placeholder;
                });
            }

            // Escape HTML entities to prevent raw < > & from being interpreted as HTML by v-html
            message = escapeHtml(message);

            // newline to br
            message = message.replace(/(\r\n|\n|\r|\\n)/gm, '<br />');

            // links
            /* eslint no-useless-escape: "off" */
            message = message.replace(/(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi, function (url) {
                var display = url.replace(/(\b(https?|ftp|file):\/\/)/gi, '');
                //var protocol = RegExp.$1;
                if (display.length > 16) display = display.substr(0, 16) + '...';

                return '<a href="' + url + '" target="_blank">' + display + '</a>';
            });

            // Restore preserved <a> tags after linkification, which has no
            // guard against URLs already inside an anchor; the placeholders
            // contain no URL so they pass through untouched.
            for (let i = 0; i < anchorPlaceholders.length; i++) {
                message = message.replace('__ANCHOR_' + i + '__', anchorPlaceholders[i]);
            }
        }

        // Wrap emoticon unicode characters in spans for styling
        if (Message.emoticonUnicodeSet) {
            for (const emoticon of Message.emoticonUnicodeSet) {
                message = message.replace(new RegExp(emoticon, 'g'), '<span class="emoticon">' + emoticon + '</span>');
            }
        }

        // newline to br
        if (type == 'created') {
            message = message.replace(/(\r\n|\n|\r|\\n)/gm, '<br />');
        }

        this.message = message;
        this.date = date;
        this.nickname = nickname || '';
        this.color = color || '';
    }
}
</script>

<style lang="scss">
@import '@/styles/global';

$chat-message-time-color: #666 !default;
$chat-bubble-border-radius: 10px !default;
$chat-bubble-padding: 6px 16px !default;
$chat-bubble-margin-top: 22px !default;
$chat-bubble-margin-top-time: -26px !default;

.chat {
    display: flex;
    flex-direction: column;

    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;

    .main {
        flex-grow: 1;

        display: flex;
        flex-direction: column;

        overflow: auto;
    }

    &__queue {
        flex-grow: 1;
        margin: 40px 20px;
        text-align: center;

        &__text {
            width: 80%;
            margin: 0 auto;
        }
    }

    &__card {
        flex-grow: 1;
        margin: 40px 20px;
        text-align: center;
    }

    &__messages {
        flex-grow: 1;
        overflow: auto;

        min-height: 200px;

        margin: 10px 10px 0 10px;
        padding: 10px 10px 5px 10px;
    }

    &__message {
        display: flex;
        flex-direction: row-reverse;

        margin-bottom: 16px;

        div {
            flex-grow: 1;
        }
        > span {
            margin-left: 16px;
            font-style: italic;
        }
        a {
            color: inherit;
            text-decoration: underline;
        }

        &--welcome {
            font-weight: bold;
        }

        &--volunteer {
            color: darken($primary-color, 20%);
        }

        &--me {
        }

        .emoticon {
            font-style: normal;
            font-size: 18px;
            vertical-align: middle;
        }
    }

    &__messages--bubbles {
        .chat__message {
            clear: both;
            position: relative;

            margin-top: $chat-bubble-margin-top;
            padding: $chat-bubble-padding;
            border-radius: $chat-bubble-border-radius;

            > span {
                position: absolute;
                margin-top: $chat-bubble-margin-top-time;
                font-size: 12px;
                font-weight: normal;
                color: $chat-message-time-color;
                white-space: nowrap;
            }

            &--welcome {
                float: left;
                background-color: $chat-bubble-color-bg-volunteer;
                color: $chat-bubble-color-volunteer;
                border-bottom-left-radius: 0;
                font-weight: normal;

                span {
                    left: -4px;
                }
            }

            &--volunteer {
                float: left;
                background-color: $chat-bubble-color-bg-volunteer;
                color: $chat-bubble-color-volunteer;
                border-bottom-left-radius: 0;

                span {
                    left: -4px;
                }
            }

            &--notification {
                margin-top: 10px;
                flex-direction: row;

                span {
                    left: -4px;
                    margin-top: -12px;
                }
            }

            &--me {
                float: right;
                background-color: $chat-bubble-color-bg-me;
                color: $chat-bubble-color-me;
                border-bottom-right-radius: 0;

                span {
                    right: 10px;
                }
            }

            &--typing {
                float: left;
                background-color: $chat-bubble-color-bg-volunteer;
                color: $chat-bubble-color-volunteer;
                border-bottom-left-radius: 0;

                span {
                    left: -4px;
                }

                div {
                    display: flex;
                    align-items: center;
                    height: 19px;
                }

                .chat__message--typeing-dot {
                    animation: typingAnimation 1.8s infinite ease-in-out;
                    background-color: #000;
                    border-radius: 9999px;
                    height: 6px;
                    width: 6px;
                    margin-top: 4px;
                    margin-right: 4px;
                }
                .chat__message--typeing-dot:nth-child(1) {
                    animation-delay: 200ms;
                }
                .chat__message--typeing-dot:nth-child(2) {
                    animation-delay: 300ms;
                }
                .chat__message--typeing-dot:nth-child(3) {
                    animation-delay: 400ms;
                }
                .chat__message--typeing-dot:last-child {
                    margin-right: 0;
                }
            }
        }
    }

    @keyframes typingAnimation {
        0% {
            transform: translateY(0px);
            background-color: #000;
        }
        28% {
            transform: translateY(-7px);
            background-color: #ccc;
        }
        44% {
            transform: translateY(0px);
            background-color: #000;
        }
    }

    /* colors */
    @mixin chat-color($name, $color, $text, $focus) {
        .chat__message--color-#{$name} {
            background-color: #{$color};
            color: $text;
        }
    }

    $white: #fff;
    $black: #000;

    /*$turkos: #43c6db;
    $violett: #8d38c9;
    $grön: #008000;
    $blå: #0000ff;
    $indigo: #4b0082;
    $röd: #ff0000;
    $orange: #ffa500;
    $lila: #800080;
    $teal: #008080;
    $lime: #00ff00;
    $magenta: #ff00ff;
    $olivegrönt: #808000;
    $inteoliv: #225667;
    $rosa: #f660ab;
    $turkos: #43c6db;

    @include chat-color('violett', $violett, $white, $white);
    @include chat-color('grön', $grön, $white, $white);
    @include chat-color('blå', $blå, $white, $white);
    @include chat-color('indigo', $indigo, $white, $white);
    @include chat-color('röd', $röd, $black, $black);
    @include chat-color('orange', $orange, $black, $black);
    @include chat-color('lila', $lila, $white, $white);
    @include chat-color('lime', $lime, $white, $white);
    @include chat-color('teal', $teal, $white, $white);
    @include chat-color('magenta', $magenta, $black, $black);
    @include chat-color('olivegrönt', $olivegrönt, $white, $white);
    @include chat-color('inteoliv', $inteoliv, $white, $white);
    @include chat-color('rosa', $rosa, $black, $black);
    @include chat-color('turkos', $turkos, $black, $black);
    */

    @include chat-color('grå', '#9E9E9E', $white, $white);
    @include chat-color('brun', '#795548', $white, $white);
    @include chat-color('blågrå', '#607D8B', $white, $white);
    @include chat-color('grön', '#4CAF50', $white, $white);
    @include chat-color('ljusgrön', '8BC34A', $white, $white);
    @include chat-color('lime', '#CDDC39', $white, $white);
    @include chat-color('cyan', '#00BCD4', $white, $white);
    @include chat-color('kricka', '#009688', $white, $white);
    @include chat-color('röd', '#F44336', $white, $white);
    @include chat-color('rosa', '#E91E63', $white, $white);
    @include chat-color('lila', '#9C27B0', $white, $white);
    @include chat-color('blå', '#2196F3', $white, $white);
    @include chat-color('djup-lila', '#673AB7', $white, $white);
    @include chat-color('indigo', '#3F51B5', $white, $white);
    @include chat-color('ljusblå', '#4FC3F7', $white, $white);

    &__typing {
        margin: 6px 20px;

        font-style: italic;
        font-size: 14px;
        line-height: 1;
    }

    &__error {
        margin: 0 20px 6px 20px;
        font-style: italic;
        font-size: 14px;
        line-height: 1;
        color: red;
    }

    &__postmessage {
        height: 100px;
        width: 100%;

        margin-bottom: 20px;
        padding: 10px;
        border: 1px solid #aaa;

        resize: none;

        background-color: #fefefe;
        box-shadow: inset 0 1px 2px rgba(10, 10, 10, 0.1);
        transition: box-shadow 0.5s, border-color 0.25s ease-in-out;

        font-family: inherit;
        font-size: 16px;
        font-weight: 400;

        @media only screen and (max-width: ($chat-width + 60)) {
            height: 60px;
        }

        &:focus {
            border-color: #000;
            outline: none;
        }

        &::placeholder {
            color:#bbb;
            font-style: italic;
            font-size:14px;
        }
    }

    &__emoticons {
        position: absolute;
        right: 5px;
        height: 26px;
        width: 34px;
        margin-top: -55px;
        overflow: hidden;

        background-color: #fff;

        cursor: pointer;
        font-size: 20px;

        transition: width 0.3s;

        svg {
            display: block;
            position: absolute;
            right: 3px;
            top: 1px;
            width: 22px;
            height: 22px;
        }

        span {
            float: right;
            position: relative;
            display: block;
            padding: 1px;
            display: none;
            user-select: none;
            z-index: 11;
            line-height: 1.2;

            &:first-child {
                display: block;
            }
        }

        &__overlay {
            position: fixed;
            display: none;
            left: 0;
            right: 0;
            top: 0;
            bottom: 0;
            z-index: 10;
        }

        &--visible {
            width: calc(100% - 10px);

            svg {
                display: none;
            }

            span {
                display: block;
            }

            .chat__emoticons__overlay {
                display: block;
            }
        }
    }
    .chat__emoticons--symbols.chat__emoticons--visible {
        display: flex;
        flex-wrap: wrap;
        align-content: baseline;
        transition: none;

        top: 0px;
        height: 100px;
        width: 100%;
        right: auto;
        padding: 10px;
        border: 1px solid #aaa;
        margin-top: 20px;
    }

    &__ended {
        height: 100px;
        margin-bottom: 20px;
        padding: 10px;
        border: 1px solid #ddd;

        font-size: 16px;
        font-weight: 400;
        font-style: italic;
    }

    &__actions {
        position: relative;
        margin: 8px 20px 20px 20px;
        border-top: 1px solid #ddd;
        padding-top: 20px;
    }

    &__buttons {
        display: flex;
        justify-content: space-between;

        &--one {
            justify-content: flex-end;
        }
    }
}

.dialog {
    &__container {
        position: fixed;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        z-index: 97;

        overflow-y: scroll;
        overflow-x: hidden;
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    &--vertically-center {
        justify-content: center;
    }

    &__overlay {
        position: fixed;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        background-color: #fff;
        @media only screen and (min-width: ($chat-width + 60)) {
            background-color: rgba(255, 255, 255, 0.6);
        }
    }

    position: relative;
    max-width: 480px;
    z-index: 99;
    padding: 30px;
    background-color: #fff;

    @media only screen and (min-width: ($chat-width + 60)) {
        margin: 90px 30px 30px 30px;
        border: 2px solid $primary-color;
        border-radius: $default-border-radius;

        &--large {
            max-width: 720px;
        }
    }

    &__close {
        position: absolute;
        top: 10px;
        right: 12px;
        cursor: pointer;

        &:hover {
            color: $primary-color;
        }

        svg {
            display: block;
            width: 16px;
        }
    }

    &__heading {
        margin-bottom: 20px;
    }

    &__error-message {
        position: relative;
        z-index: 99;
        margin-top: 12px;
        padding: 8px 16px;
        background-color: #fff;
        border-radius: $default-border-radius;
        color: red;
    }

    &__text {
        @media only screen and (max-width: ($chat-width + 60)) {
            font-size: 14px;

            p {
                font-size: 14px;
            }
        }
    }

    &__actions {
        display: flex;
        justify-content: center;
        align-items: center;

        margin-top: 20px;

        button.button {
            margin-left: 20px;

            &:first-child {
                margin-left: 0;
            }
        }

        &--space-beetween {
            justify-content: space-between;
        }
    }
}

.dialog--fade-enter-active,
.dialog--fade-leave-active {
    transition: opacity 0.5s;
}
.dialog--fade-enter,
.dialog--fade-leave-to {
    opacity: 0;
}

.form {
    /*display: flex;
        flex-direction: column;

        @media only screen and (min-width: ($chat-width + 60)) {
            flex-direction: row;
            flex-wrap: wrap;
        }*/

    &__field {
        margin-bottom: 25px;
        //width:100%;

        label {
            display: block;
        }

        &__label {
            font-weight: bold !important;
            margin-bottom: 10px;

            span {
                display: block;
                font-size: 14px;
                font-weight: normal;
            }

            em {
                display: block;
                font-size: 14px;
                font-weight: normal;
                color: $primary-color;
            }

            small {
                font-weight: normal;
            }

            @media only screen and (max-width: ($chat-width + 60)) {
                font-size: 14px;

                span,
                em {
                    font-size: 12px;
                }
            }
        }

        &__input {
            margin-bottom: 15px;

            input[type='text'],
            input[type='number'],
            textarea,
            select {
                font-family: $body-font-famlily;
                padding: 10px;
                font-size: 15px;

                width: 100%;
            }

            &--radiobuttons,
            &--checkboxes {
                label {
                    display: flex;
                    align-items: baseline;
                    margin-bottom: 5px;
                    flex-wrap: nowrap;
                }

                input[type='radio'],
                input[type='checkbox'] {
                    margin: 3px 7px 0 12px;
                }

                span {
                    width: 100%;
                }

                div {
                    width: 100%;

                    input[type='text'],
                    input[type='number'] {
                        width: 50%;
                        margin: 5px 0 10px 0;
                        padding: 5px;
                    }
                }
            }

            @media only screen and (min-width: ($chat-width + 60)) {
                &--horizontal {
                    display: flex;
                    label:first-child {
                        input:first-child {
                            margin-left: 0;
                        }
                    }
                }
            }
        }

        &__table {
            margin-top: 20px;

            th {
                padding: 3px 10px 8px 10px;
                text-align: center;
                font-weight: normal;
                vertical-align: bottom;
                &:first-child {
                    padding-left: 0;
                }

                &.form__row__label {
                    text-align: left;
                    font-style: italic;
                }
            }
            td {
                padding: 3px 10px;
                text-align: center;

                &:first-child {
                    text-align: left;
                    padding-left: 0;
                }

                input[type='radio'] {
                    display: inline-block;
                }
            }

            &--bordered {
                border-collapse: collapse;

                th,
                td {
                    border: 1px solid #ddd;

                    &:first-child {
                        padding-left: 0 !important;
                        border-left-width: 0;
                    }
                    &.form__field__label {
                        padding: 10px 0 10px 0 !important;
                        font-weight: bold !important;
                    }
                }

                tr:first-child {
                    th {
                        border-top-width: 0;
                    }
                }
                tr:last-child {
                    th {
                        border-top-width: 0;
                    }
                }
            }
        }

        &--required {
            .form__field__label {
                &::after {
                    content: '*';
                    color: red;
                }
            }
        }
        &--error {
            .form__field__label {
                color: red;
            }
        }
        &__error {
            margin-top: -10px;
            color: red;
            font-size: 14px;
        }
    }

    &__progress {
        display: block;
        min-width: 100px;
        border-top: 4px solid $primary-color;
        margin: -31px 0 30px 0;
        padding-top: 10px;
    }
}

.dialog--wizard {
    .form {
        margin: 30px 0;
        padding: 30px 0;
        border-top: 1px solid #ddd;
        border-bottom: 1px solid #ddd;
    }
}
</style>
